import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import styles from './Specifications.module.css';

import {
  capitalizeFirstLetterEachWord,
  isEmptyList
} from '../../../../../Utils/helper-functions';

import { PageText } from "../../../../../components/Text/Text";
import { AnimatedComponent } from '../../../../../Hooks/use-framer-motion';
import FeatureDefinitionText from "../../../../../Shared/Components/FeatureDefinitionText/FeatureDefinitionText";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
    }
  }
};

const SpecsContainer = ({ product, print = false }) => {
  const specListGroup1 = product.specList1;
  const specTitle1 = product.specTitle1;

  const specListGroup2 = product.specList2;
  const specTitle2 = product.specTitle2;

  const specListGroup3 = product.specList3;
  const specTitle3 = product.specTitle3;

  const specListGroup4 = product.specList4;
  const specTitle4 = product.specTitle4;

  const specListStyles = print === true
    ? {
        ulStyles: styles.printUl,
        liStyles: styles.printLi,
        liHeaderStyles: styles.liHeaderStyles,
      }
    : {
        ulStyles: styles.specListWrapper,
        liStyles: styles.specListItem,
        liHeaderStyles: styles.liHeaderStyles,
      };

  const deactivateAnimation = print === true ? undefined : "hidden";

  const titleRef1 = useRef(null);
  const listRef1 = useRef(null);
  const titleInView1 = useInView(titleRef1, { once: false, threshold: 0.5 });
  const listInView1 = useInView(listRef1, { once: false, threshold: 0.5 });

  const titleRef2 = useRef(null);
  const listRef2 = useRef(null);
  const titleInView2 = useInView(titleRef2, { once: false, threshold: 0.5 });
  const listInView2 = useInView(listRef2, { once: false, threshold: 0.5 });

  const titleRef3 = useRef(null);
  const listRef3 = useRef(null);
  const titleInView3 = useInView(titleRef3, { once: false, threshold: 0.5 });
  const listInView3 = useInView(listRef3, { once: false, threshold: 0.5 });

  const titleRef4 = useRef(null);
  const listRef4 = useRef(null);
  const titleInView4 = useInView(titleRef4, { once: false, threshold: 0.5 });
  const listInView4 = useInView(listRef4, { once: false, threshold: 0.5 });

  const has1 = !isEmptyList(specListGroup1);
  const has2 = !isEmptyList(specListGroup2);
  const has3 = !isEmptyList(specListGroup3);
  const has4 = !isEmptyList(specListGroup4);

  const renderSpecText = (text) => {
    if (print) {
      return <PageText type="productPageSpecList">{text}</PageText>;
    }

    return (
      <FeatureDefinitionText
        text={text}
        product={product}
        textType="productPageSpecList"
        categoryRestrictive={true}
      />
    );
  };

  return (
    <div className={styles.specificationComponentContainer}>
      {has1 && (
        <div>
          <span className={styles.printSpecListTitle}>
            {capitalizeFirstLetterEachWord(specTitle1)}
          </span>

          <div className={styles.specListTitle} ref={titleRef1}>
            {titleInView1 && (
              <AnimatedComponent
                type="wipeEffect"
                directionStart="left"
                delay={0.1}
              >
                <PageText type="productPageSpecTitle">
                  {capitalizeFirstLetterEachWord(specTitle1)}
                </PageText>
              </AnimatedComponent>
            )}
          </div>

          <motion.ul
            className={specListStyles.ulStyles}
            ref={listRef1}
            variants={containerVariants}
            initial={deactivateAnimation}
            animate={listInView1 ? "visible" : "hidden"}
          >
            {specListGroup1.map((e, idx) => (
              <motion.li
                className={specListStyles.liStyles}
                key={idx}
                variants={itemVariants}
              >
                {renderSpecText(e)}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      )}

      {has2 && (
        <div>
          <span className={styles.printSpecListTitle}>
            {capitalizeFirstLetterEachWord(specTitle2)}
          </span>

          <div className={styles.specListTitle} ref={titleRef2}>
            {titleInView2 && (
              <AnimatedComponent
                type="wipeEffect"
                directionStart="left"
                delay={0.1}
              >
                <PageText type="productPageSpecTitle">
                  {capitalizeFirstLetterEachWord(specTitle2)}
                </PageText>
              </AnimatedComponent>
            )}
          </div>

          <motion.ul
            className={specListStyles.ulStyles}
            ref={listRef2}
            variants={containerVariants}
            initial={deactivateAnimation}
            animate={listInView2 ? "visible" : "hidden"}
          >
            {specListGroup2.map((e, idx) => (
              <motion.li
                className={specListStyles.liStyles}
                key={idx}
                variants={itemVariants}
              >
                {renderSpecText(e)}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      )}

      {has3 && (
        <div>
          <span className={styles.printSpecListTitle}>
            {capitalizeFirstLetterEachWord(specTitle3)}
          </span>

          <div className={styles.specListTitle} ref={titleRef3}>
            {titleInView3 && (
              <AnimatedComponent
                type="wipeEffect"
                directionStart="left"
                delay={0.1}
              >
                <PageText type="productPageSpecTitle">
                  {capitalizeFirstLetterEachWord(specTitle3)}
                </PageText>
              </AnimatedComponent>
            )}
          </div>

          <motion.ul
            className={specListStyles.ulStyles}
            ref={listRef3}
            variants={containerVariants}
            initial={deactivateAnimation}
            animate={listInView3 ? "visible" : "hidden"}
          >
            {specListGroup3.map((e, idx) => (
              <motion.li
                className={specListStyles.liStyles}
                key={idx}
                variants={itemVariants}
              >
                {renderSpecText(e)}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      )}

      {has4 && (
        <div>
          <span className={styles.printSpecListTitle}>
            {capitalizeFirstLetterEachWord(specTitle4)}
          </span>

          <div className={styles.specListTitle} ref={titleRef4}>
            {titleInView4 && (
              <AnimatedComponent
                type="wipeEffect"
                directionStart="left"
                delay={0.1}
              >
                <PageText type="productPageSpecTitle">
                  {capitalizeFirstLetterEachWord(specTitle4)}
                </PageText>
              </AnimatedComponent>
            )}
          </div>

          <motion.ul
            className={specListStyles.ulStyles}
            ref={listRef4}
            variants={containerVariants}
            initial={deactivateAnimation}
            animate={listInView4 ? "visible" : "hidden"}
          >
            {specListGroup4.map((e, idx) => (
              <motion.li
                className={specListStyles.liStyles}
                key={idx}
                variants={itemVariants}
              >
                {renderSpecText(e)}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      )}
    </div>
  );
};

export default SpecsContainer;